import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowDownToLine, ArrowUpFromLine, Send, Loader2, Coins, Gift, QrCode, Copy } from "lucide-react";

const PACKAGES = [
  { key: "dep_10", value: 10 }, { key: "dep_25", value: 25 }, { key: "dep_50", value: 50 },
  { key: "dep_100", value: 100 }, { key: "dep_200", value: 200 },
];
const VPACKS = [
  { key: "vf_5000", fichas: 5000, price: 5 }, { key: "vf_10000", fichas: 10000, price: 9 },
  { key: "vf_30000", fichas: 30000, price: 25 }, { key: "vf_50000", fichas: 50000, price: 40 },
];
const TYPE_LABEL = {
  DEPOSITO: "Depósito", ENTRADA_PARTIDA: "Entrada em partida", PREMIO_VITORIA: "Prêmio",
  REEMBOLSO: "Reembolso", SAQUE_SOLICITADO: "Saque solicitado", SAQUE_REJEITADO: "Saque estornado",
  AJUSTE_ADMIN: "Ajuste admin", TRANSFERENCIA_ENVIADA: "Transferência enviada", TRANSFERENCIA_RECEBIDA: "Transferência recebida",
  BONUS_FICHAS: "Fichas grátis", COMPRA_FICHAS: "Compra de fichas", ADMIN_FICHAS: "Fichas do admin",
};

export default function WalletPage() {
  const { user, refresh } = useAuth();
  const [txs, setTxs] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const [wAmount, setWAmount] = useState("");
  const [pix, setPix] = useState("");
  const [tTo, setTTo] = useState("");
  const [tAmt, setTAmt] = useState("");
  const [pixAmount, setPixAmount] = useState("");
  const [cpf, setCpf] = useState("");
  const [mpPix, setMpPix] = useState(null);
  const [mpLoading, setMpLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [confirmW, setConfirmW] = useState(false);
  const [tWallet, setTWallet] = useState("real");
  const [buyPkg, setBuyPkg] = useState(null);
  const [pin, setPinCode] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/user/transactions");
    setTxs(data);
    refresh();
  }, [refresh]);

  useEffect(() => { load(); }, [load]);

  const deposit = async (pkg) => {
    setLoadingKey(pkg.key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: pkg.key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Erro ao iniciar depósito.");
      setLoadingKey(null);
    }
  };

  const withdraw = async () => {
    const amt = parseFloat(wAmount);
    if (!amt || amt <= 0) return toast.error("Informe um valor válido.");
    if (pix.replace(/\D/g, "").length !== 11) return toast.error("A chave Pix deve ser o seu CPF (11 dígitos).");
    if (pin.length !== 4) return toast.error("Informe seu PIN de 4 dígitos.");
    try {
      const { data } = await api.post("/wallet/withdraw", { amount: amt, pix_key: pix, pin });
      toast.success(data.message || "Saque solicitado! Pagamento em até 24h.");
      setWAmount(""); setPix(""); setPinCode(""); setConfirmW(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro no saque."); }
  };

  const transfer = async () => {
    const amt = parseFloat(tAmt);
    if (!amt || amt <= 0) return toast.error("Valor inválido.");
    if (!tTo.trim()) return toast.error("Informe o ID do destinatário.");
    if (pin.length !== 4) return toast.error("Informe seu PIN de 4 dígitos.");
    try {
      await api.post("/wallet/transfer", { to_user_id: tTo, amount: amt, wallet: tWallet, pin });
      toast.success("Transferência enviada.");
      setTTo(""); setTAmt(""); setPinCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro na transferência."); }
  };

  const buyStripe = async (pkg) => {
    try {
      const { data } = await api.post("/payments/checkout-virtual", { package: pkg.key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Erro ao iniciar compra."); }
  };

  const buyPix = async (pkg) => {
    if (cpf.replace(/\D/g, "").length !== 11) return toast.error("Informe um CPF válido (11 dígitos).");
    setMpLoading(true);
    try {
      const { data } = await api.post("/wallet/pix/deposit", { package: pkg.key, cpf });
      setBuyPkg(null); setMpPix(data); pollPix(data.payment_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar Pix."); }
    setMpLoading(false);
  };

  const mpDeposit = async () => {
    const amt = parseFloat(pixAmount);
    if (!amt || amt < 1) return toast.error("Valor mínimo R$ 1,00.");
    if (cpf.replace(/\D/g, "").length !== 11) return toast.error("Informe um CPF válido (11 dígitos).");
    setMpLoading(true);
    try {
      const { data } = await api.post("/wallet/pix/deposit", { amount: amt, cpf });
      setMpPix(data);
      pollPix(data.payment_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar Pix."); }
    setMpLoading(false);
  };

  const pollPix = (paymentId) => {
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/wallet/pix/status/${paymentId}`);
        if (data.credited || data.status === "approved") {
          toast.success("Pagamento confirmado! Créditos adicionados.");
          setMpPix(null); setPixAmount(""); load();
          return;
        }
      } catch {}
      if (tries < 40) setTimeout(tick, 4000);
    };
    setTimeout(tick, 4000);
  };

  const claimVirtual = async () => {
    setClaiming(true);
    try {
      await api.post("/wallet/claim-virtual");
      toast.success("Fichas grátis creditadas!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao resgatar fichas."); }
    setClaiming(false);
  };

  return (
    <div className="dr-grain min-h-screen bg-[#090B0E]">
      <Navbar />
      <main className="relative z-20 max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-head text-3xl sm:text-4xl font-black mb-6">Carteira</h1>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="dr-card p-8 relative overflow-hidden" data-testid="balance-card">
            <div className="absolute -right-8 -top-8 w-40 h-40 bg-[#D4AF37]/10 blur-3xl rounded-full" />
            <div className="flex items-center gap-2 text-[#9CA3AF] text-sm uppercase tracking-wider"><WalletIcon size={16} className="text-[#D4AF37]" /> Saldo em Reais</div>
            <div className="font-mono-num font-black text-5xl mt-2 gold-text" data-testid="balance-amount">{money(user?.credits)}</div>
            <p className="text-xs text-[#6b7280] mt-3">Seu ID: <span className="font-mono-num text-[#9CA3AF]">{user?.user_id}</span></p>
          </div>
          <div className="dr-card p-8 relative overflow-hidden" data-testid="virtual-balance-card">
            <div className="absolute -right-8 -top-8 w-40 h-40 bg-[#10B981]/10 blur-3xl rounded-full" />
            <div className="flex items-center gap-2 text-[#9CA3AF] text-sm uppercase tracking-wider"><Coins size={16} className="text-[#10B981]" /> Fichas Virtuais (diversão)</div>
            <div className="font-mono-num font-black text-5xl mt-2 text-[#10B981]" data-testid="virtual-balance-amount">{Number(user?.virtual_credits || 0).toLocaleString("pt-BR")}</div>
            <button onClick={claimVirtual} disabled={claiming || (user?.virtual_credits || 0) > 100} data-testid="claim-virtual-btn"
              className="mt-3 flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/15 text-[#10B981] text-sm font-semibold disabled:opacity-40 enabled:hover:bg-[#10B981]/25 transition-colors">
              {claiming ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />} Resgatar 5.000 fichas grátis
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="dr-card p-6">
            <h2 className="font-head text-xl font-bold flex items-center gap-2 mb-4"><ArrowDownToLine size={18} className="text-[#10B981]" /> Depositar</h2>
            <div className="grid grid-cols-2 gap-3">
              {PACKAGES.map((p) => (
                <button key={p.key} onClick={() => deposit(p)} disabled={loadingKey === p.key} data-testid={`deposit-${p.value}`}
                  className="btn-gold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                  {loadingKey === p.key ? <Loader2 size={16} className="animate-spin" /> : `R$ ${p.value}`}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#6b7280] mt-3">Pagamento seguro via Stripe (cartão de teste 4242 4242 4242 4242). Créditos liberados automaticamente.</p>
            <div className="mt-4 pt-4 border-t border-white/10">
              <h3 className="font-head font-bold text-sm flex items-center gap-2 mb-2"><QrCode size={15} className="text-[#3B82F6]" /> Depositar via Pix (Mercado Pago)</h3>
              {!mpPix ? (
                <>
                  <div className="flex gap-2 mb-2">
                    <input value={pixAmount} onChange={(e) => setPixAmount(e.target.value)} type="number" placeholder="Valor (R$)" data-testid="pix-deposit-amount"
                      className="flex-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 font-mono-num focus:border-[#3B82F6] outline-none" />
                  </div>
                  <input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="Seu CPF (só números)" data-testid="pix-deposit-cpf"
                    className="w-full bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 mb-2 font-mono-num focus:border-[#3B82F6] outline-none" />
                  <button onClick={mpDeposit} disabled={mpLoading} data-testid="pix-deposit-btn"
                    className="w-full py-2.5 rounded-lg bg-[#3B82F6]/20 text-[#93C5FD] font-semibold text-sm hover:bg-[#3B82F6]/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {mpLoading ? <Loader2 size={15} className="animate-spin" /> : <QrCode size={15} />} Gerar QR Code Pix
                  </button>
                  <p className="text-[11px] text-[#6b7280] mt-2">Créditos liberados automaticamente após o pagamento.</p>
                </>
              ) : (
                <div className="text-center" data-testid="pix-qr">
                  {mpPix.qr_code_base64 && <img src={`data:image/png;base64,${mpPix.qr_code_base64}`} alt="QR Code Pix" className="mx-auto w-48 h-48 bg-white rounded-lg p-1" />}
                  <p className="text-sm text-[#9CA3AF] mt-2">Pague <span className="font-mono-num text-[#F3E5AB]">{money(mpPix.amount)}</span> escaneando o QR ou copie o código:</p>
                  <div className="flex gap-2 mt-2">
                    <input readOnly value={mpPix.qr_code || ""} data-testid="pix-copia-cola" className="flex-1 bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2 text-xs font-mono-num truncate outline-none" />
                    <button onClick={() => { navigator.clipboard?.writeText(mpPix.qr_code || ""); toast.success("Código copiado!"); }} className="px-3 rounded-lg bg-[#3B82F6]/20 text-[#93C5FD]" data-testid="pix-copy-btn"><Copy size={15} /></button>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-3 text-xs text-[#9CA3AF]"><Loader2 size={14} className="animate-spin" /> Aguardando pagamento...</div>
                  <button onClick={() => setMpPix(null)} className="text-xs text-[#6b7280] underline mt-2" data-testid="pix-cancel-btn">Cancelar</button>
                </div>
              )}
            </div>
          </div>

          <div className="dr-card p-6">
            <h2 className="font-head text-xl font-bold flex items-center gap-2 mb-4"><ArrowUpFromLine size={18} className="text-[#F59E0B]" /> Sacar (Pix)</h2>
            <input value={wAmount} onChange={(e) => setWAmount(e.target.value)} type="number" placeholder="Valor (R$)" data-testid="withdraw-amount"
              className="w-full bg-[#0F1319] border border-white/10 rounded-lg px-4 py-3 mb-3 font-mono-num focus:border-[#D4AF37] outline-none" />
            <input value={pix} onChange={(e) => setPix(e.target.value)} placeholder="Chave Pix (CPF, só números)" data-testid="withdraw-pix"
              className="w-full bg-[#0F1319] border border-white/10 rounded-lg px-4 py-3 mb-2 font-mono-num focus:border-[#D4AF37] outline-none" />
            <p className="text-[11px] text-[#6b7280] mb-3">A chave Pix deve ser o seu CPF. Pagamento processado em até 24h.</p>
            <button onClick={() => { const amt = parseFloat(wAmount); if (!amt || amt <= 0) return toast.error("Informe um valor válido."); if (pix.replace(/\\D/g, "").length !== 11) return toast.error("Informe seu CPF (11 dígitos)."); setConfirmW(true); }} data-testid="withdraw-btn" className="w-full py-3 rounded-full border border-[#F59E0B]/50 text-[#F59E0B] font-semibold hover:bg-[#F59E0B]/10 transition-colors">Solicitar saque</button>
          </div>
        </div>

        <div className="dr-card p-6 mb-8" data-testid="buy-fichas-card">
          <h2 className="font-head text-xl font-bold flex items-center gap-2 mb-1"><Coins size={18} className="text-[#10B981]" /> Comprar Fichas Virtuais</h2>
          <p className="text-[11px] text-[#6b7280] mb-4">Fichas para o modo diversão. <span className="text-[#FCD34D]">Não podem ser sacadas em reais</span> — apenas transferidas entre jogadores.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {VPACKS.map((p) => (
              <button key={p.key} onClick={() => setBuyPkg(p)} data-testid={`buy-fichas-${p.fichas}`}
                className="bg-[#0F1319] border border-[#10B981]/30 rounded-xl p-4 text-center hover:border-[#10B981]/70 transition-colors">
                <div className="font-mono-num font-black text-xl text-[#10B981]">{p.fichas.toLocaleString("pt-BR")}</div>
                <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">fichas</div>
                <div className="font-mono-num text-sm text-[#F3E5AB] mt-2">{money(p.price)}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="dr-card p-6 mb-8">
          <h2 className="font-head text-xl font-bold flex items-center gap-2 mb-4"><Send size={18} className="text-[#D4AF37]" /> Transferir para outro jogador</h2>
          <div className="flex gap-2 mb-3" data-testid="transfer-wallet-tabs">
            <button onClick={() => setTWallet("real")} data-testid="transfer-wallet-real"
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${tWallet === "real" ? "btn-gold" : "border border-white/10 text-[#9CA3AF]"}`}>Reais (R$)</button>
            <button onClick={() => setTWallet("virtual")} data-testid="transfer-wallet-virtual"
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${tWallet === "virtual" ? "bg-[#10B981] text-black" : "border border-white/10 text-[#9CA3AF]"}`}>Fichas</button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={tTo} onChange={(e) => setTTo(e.target.value)} placeholder="ID do destinatário" data-testid="transfer-to"
              className="flex-1 bg-[#0F1319] border border-white/10 rounded-lg px-4 py-3 focus:border-[#D4AF37] outline-none" />
            <input value={tAmt} onChange={(e) => setTAmt(e.target.value)} type="number" placeholder={tWallet === "virtual" ? "Fichas" : "Valor"} data-testid="transfer-amount"
              className="sm:w-40 bg-[#0F1319] border border-white/10 rounded-lg px-4 py-3 font-mono-num focus:border-[#D4AF37] outline-none" />
            <button onClick={transfer} data-testid="transfer-btn" className="btn-gold px-6 py-3 rounded-full">Enviar</button>
          </div>
          <input value={pin} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="PIN de segurança (4 dígitos)" data-testid="transfer-pin"
            className="mt-3 w-full sm:w-64 bg-[#0F1319] border border-white/10 rounded-lg px-4 py-2.5 font-mono-num tracking-[0.3em] focus:border-[#D4AF37] outline-none" />
          <p className="text-[11px] text-[#6b7280] mt-3">A plataforma não se responsabiliza por transferências diretas entre usuários.{!user?.has_pin && " Configure seu PIN no Perfil."}</p>
        </div>

        <div className="dr-card p-6" data-testid="transactions">
          <h2 className="font-head text-xl font-bold mb-4">Extrato</h2>
          {txs.length === 0 ? <p className="text-[#9CA3AF] text-sm">Nenhuma transação ainda.</p> : (
            <div className="divide-y divide-white/5">
              {txs.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-semibold text-sm">{TYPE_LABEL[t.type] || t.type}</div>
                    <div className="text-[11px] text-[#6b7280]">{new Date(t.created_at).toLocaleString("pt-BR")}</div>
                  </div>
                  <div className={`font-mono-num font-bold ${t.amount >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                    {t.amount >= 0 ? "+" : ""}{t.wallet === "virtual" ? `${Number(t.amount).toLocaleString("pt-BR")} fichas` : money(t.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {buyPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => setBuyPkg(null)}>
          <div className="dr-card max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="buy-fichas-modal">
            <h3 className="font-head font-bold text-xl mb-1 flex items-center gap-2"><Coins size={18} className="text-[#10B981]" /> Comprar {buyPkg.fichas.toLocaleString("pt-BR")} fichas</h3>
            <p className="text-sm text-[#9CA3AF] mb-4">Valor: <span className="font-mono-num text-[#F3E5AB]">{money(buyPkg.price)}</span> • crédito automático após pagamento.</p>
            <button onClick={() => buyStripe(buyPkg)} data-testid="buy-fichas-stripe" className="w-full btn-gold py-3 rounded-full mb-3">Pagar com Cartão (Stripe)</button>
            <div className="pt-3 border-t border-white/10">
              <p className="text-xs text-[#9CA3AF] mb-2">Ou pague via Pix (Mercado Pago):</p>
              <input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="Seu CPF (só números)" data-testid="buy-fichas-cpf"
                className="w-full bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 mb-2 font-mono-num focus:border-[#3B82F6] outline-none" />
              <button onClick={() => buyPix(buyPkg)} disabled={mpLoading} data-testid="buy-fichas-pix"
                className="w-full py-2.5 rounded-lg bg-[#3B82F6]/20 text-[#93C5FD] font-semibold text-sm hover:bg-[#3B82F6]/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {mpLoading ? <Loader2 size={15} className="animate-spin" /> : <QrCode size={15} />} Gerar QR Code Pix
              </button>
            </div>
            <button onClick={() => setBuyPkg(null)} className="w-full mt-4 py-2.5 rounded-full border border-white/10 text-sm">Cancelar</button>
          </div>
        </div>
      )}

      {confirmW && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => setConfirmW(false)}>
          <div className="dr-card max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="withdraw-confirm-modal">
            <h3 className="font-head font-bold text-xl mb-3 flex items-center gap-2"><ArrowUpFromLine size={18} className="text-[#F59E0B]" /> Confirmar saque</h3>
            <p className="text-sm text-[#d1d5db]">Você está solicitando o saque de <span className="font-mono-num text-[#F3E5AB]">{money(parseFloat(wAmount) || 0)}</span> para a chave Pix (CPF) <span className="font-mono-num text-[#10B981]">{pix}</span>.</p>
            <p className="text-[11px] text-[#6b7280] mt-2">O valor será reservado do seu saldo e o pagamento processado pela banca em até 24h.</p>
            <input value={pin} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="PIN de segurança (4 dígitos)" data-testid="withdraw-pin"
              className="mt-4 w-full bg-[#0F1319] border border-white/10 rounded-lg px-4 py-2.5 font-mono-num tracking-[0.3em] focus:border-[#F59E0B] outline-none" />
            {!user?.has_pin && <p className="text-[11px] text-[#F59E0B] mt-2">Você ainda não configurou um PIN. Vá ao Perfil para criar.</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setConfirmW(false)} className="flex-1 py-2.5 rounded-full border border-white/10">Cancelar</button>
              <button onClick={withdraw} data-testid="withdraw-confirm-btn" className="flex-1 btn-gold py-2.5 rounded-full">Confirmar saque</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
